import {
  SUPPORT_INSIGHT_METRIC_SLOT_COUNT,
  type SupportInsightMetricLocationId,
} from "@redotech/redo-support-settings-common/support-insight-slots";

/**
 * Metric cards on the merchant Support dashboard tab, in page order.
 * Titles match RedoMetricItem titles in the support dashboard.
 */
export const supportInsightLocations = [
  {
    id: "ticket-automation-rate",
    label: "Ticket automation rate",
    recommended: true,
  },
  { id: "total-tickets-automated", label: "Total tickets automated" },
  {
    id: "potential-cost-savings",
    label: "Potential cost savings",
    recommended: true,
  },
  { id: "total-tickets", label: "Total tickets" },
  { id: "created-tickets", label: "Created tickets" },
  { id: "closed-tickets", label: "Closed tickets" },
  { id: "resolved-tickets", label: "Resolved tickets" },
  { id: "median-first-response-time", label: "Median first response time" },
  { id: "median-first-human-response", label: "Median first human response" },
  {
    id: "median-time-to-resolution",
    label: "Median time to resolution",
    recommended: true,
  },
] as const;

/** First three metric cards, for the configurable location spots. */
export function defaultInsightLocations(): SupportInsightMetricLocationId[] {
  return supportInsightLocations
    .slice(0, SUPPORT_INSIGHT_METRIC_SLOT_COUNT)
    .map((location) => location.id);
}

/** Puts `id` in `index`. A card already used by another spot stays where it is. */
export function assignInsightLocation(
  locations: readonly SupportInsightMetricLocationId[],
  index: number,
  id: SupportInsightMetricLocationId,
): SupportInsightMetricLocationId[] {
  const next = defaultInsightLocations().map(
    (fallback, slot) => locations[slot] ?? fallback,
  );
  const current = next[index];
  if (current === undefined || current === id) {
    return next;
  }
  const taken = next.some((value, slot) => slot !== index && value === id);
  if (taken) {
    return next;
  }
  next[index] = id;
  return next;
}

export function insightLocationsUsedElsewhere(
  locations: readonly SupportInsightMetricLocationId[],
  index: number,
): { value: SupportInsightMetricLocationId; tooltip: string }[] {
  return locations.flatMap((id, slot) =>
    slot === index
      ? []
      : [{ value: id, tooltip: "Already chosen for another insight." }],
  );
}
